import { useState, useMemo } from "react";
import {
  useJobBulkActions,
  UseJobBulkActionsResult,
} from "./use-job-bulk-actions";
import { calculateDerivedStats, DerivedStats } from "../helpers";
import { useJobGrouping, UseJobGroupingReturn } from "./use-job-grouping";
import { useJobListData, UseJobListDataReturn } from "./use-job-list-data";
import {
  useJobListFilters,
  UseJobListFiltersReturn,
} from "./use-job-list-filters";
import { useJobListStats, UseJobListStatsReturn } from "./use-job-list-stats";
import { useSheetData, UseSheetDataReturn } from "./use-sheet-data";
import {
  useJobListPolling,
  UseJobListPollingReturn,
} from "./use-job-list-polling";
import { JobStatus } from "../../../../pages/playground/types";

export interface UseGlobalJobListDataReturn
  extends UseJobListDataReturn,
    UseSheetDataReturn,
    UseJobListStatsReturn,
    DerivedStats,
    UseJobListFiltersReturn,
    UseJobGroupingReturn,
    UseJobListPollingReturn,
    UseJobBulkActionsResult {
  configurationCompleted: boolean;
  canPreProcess: boolean;
  isCompleted: boolean;
  showKeywordSearch: boolean;
  toggleKeywordSearch: () => void;
  hasPendingTranslations: boolean;
}

/**
 * Hook for global help
 */
export function useGlobalJobListData(batchId: string | undefined) {
  const [showKeywordSearch, setShowKeywordSearch] = useState(false);

  const jobListData = useJobListData(batchId);
  const sheetData = useSheetData(jobListData.batch);
  const jobListStats = useJobListStats(batchId);
  const derivedStats = calculateDerivedStats(jobListStats.stats);
  const jobListFilters = useJobListFilters();
  const filteredAndGroupedJobs = useJobGrouping(
    jobListData.jobs,
    jobListFilters.statusFilter,
    jobListFilters.columnFilter,
    sheetData.sheet
  );

  const toggleKeywordSearch = () => {
    setShowKeywordSearch((prev) => !prev);
  };

  const bulkActions = useJobBulkActions({
    onRefresh: async () => {
      await jobListData.refetch();
      await jobListStats.reloadStats();
    },
  });

  const hasGdriveFolder = !!jobListData?.batch?.gdrive_folder_id;

  // Single flag from backend that indicates batch configuration is complete
  // (master file descriptions confirmed + custom bullet points set)
  const configurationCompleted =
    jobListData?.batch?.batch_configuration_completed || false;

  // Check if there are pending translations (completed jobs without has_ai_translations)
  const hasPendingTranslations = useMemo(() => {
    const isClientOwned = jobListData.batch?.client_owned;
    const laraLanguages = jobListData.batch?.settings?.lara_languages;

    if (!isClientOwned) return false;

    if (!laraLanguages || laraLanguages.length === 0) return false;

    // Check if any completed job is missing translations
    return (
      jobListData.jobs?.some(
        (job) =>
          job.status === JobStatus.COMPLETED &&
          job.has_ai_translations === false
      ) ?? false
    );
  }, [
    jobListData.jobs,
    jobListData.batch?.settings?.lara_languages,
    jobListData.batch?.client_owned,
  ]);

  // Poll for jobs only when configuration is complete AND there's work to do
  const shouldBePolling: boolean =
    (configurationCompleted &&
      ((!hasGdriveFolder &&
        sheetData.sheet &&
        sheetData.sheet.items.length !== derivedStats.totalLoadedJobs) ||
        (hasGdriveFolder &&
          (derivedStats.atLeastOneRealMissingGarmentType ||
            derivedStats.atLeastOneJobAiProcessing)))) ||
    hasPendingTranslations;

  const jobListPolling = useJobListPolling(shouldBePolling, async () => {
    await jobListData.refetch();
    await jobListStats.reloadStats();
  });

  const canPreProcess =
    hasGdriveFolder &&
    configurationCompleted &&
    !jobListPolling.isPolling &&
    derivedStats.atLeastOneJobMissingImages;

  const isCompleted =
    configurationCompleted &&
    ((!hasGdriveFolder && derivedStats.totalLoadedJobs > 0) ||
      (hasGdriveFolder &&
        jobListStats.totalItems > 0 &&
        !derivedStats.atLeastOneRealMissingGarmentType &&
        !derivedStats.atLeastOneJobAiProcessing));

  return {
    ...jobListData,
    ...sheetData,
    ...jobListStats,
    derivedStats,
    ...jobListFilters,
    filteredAndGroupedJobs,
    ...bulkActions,
    ...jobListPolling,
    configurationCompleted,
    canPreProcess,
    isCompleted,
    showKeywordSearch,
    toggleKeywordSearch,
    hasPendingTranslations,
  };
}
